import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShoppingCart, CheckCircle, CreditCard, Minus, Plus, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { OptionChips, autoPick, missingOption } from '../components/store/OptionChips';
import { ComboPickerDialog } from '../components/store/ComboPickerDialog';
import { CartLineOptions } from '../components/store/OrderItemsDetail';
import LanguageToggle from '../components/LanguageToggle';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lineKey = (l) => l.is_combo ? `combo__${l.uid}` : `${l.product_id}__${l.size || ''}__${l.color || ''}`;

const effUnit = (basePrice, tiers, qty) => {
  let best = Number(basePrice) || 0, bestMin = 1;
  (tiers || []).forEach((t) => {
    const mq = parseInt(t.min_qty), up = parseFloat(t.unit_price);
    if (mq >= 1 && up > 0 && qty >= mq && mq >= bestMin) { bestMin = mq; best = up; }
  });
  return best;
};

export default function StorePublic() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.get('success') === 'true';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]); // [{product_id, product_name, size, color, quantity, price}]
  const [showCheckout, setShowCheckout] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [paymentMode, setPaymentMode] = useState('full'); // full | deposit
  const [zelleStep, setZelleStep] = useState(false);
  const [zelleOrder, setZelleOrder] = useState(null);
  const [uploadingZelle, setUploadingZelle] = useState(false);
  // Per-product transient picker for add-to-cart
  const [pickers, setPickers] = useState({}); // product_id -> {size, color}
  const [combos, setCombos] = useState([]);
  const [activeImg, setActiveImg] = useState({}); // product_id -> index
  const [comboPicking, setComboPicking] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [prodRes, comboRes] = await Promise.all([
          axios.get(`${API}/public/products`),
          axios.get(`${API}/public/store-combos`),
        ]);
        setProducts(prodRes.data || []);
        setCombos(comboRes.data || []);
      } catch {
        toast.error(t('common.errorProcessing'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addComboToCart = (combo, selections) => {
    setCart((prev) => [...prev, { uid: `${combo.id}-${Date.now()}`, combo_id: combo.id, is_combo: true, product_name: combo.name, price: combo.price, basePrice: combo.price, quantity: 1, selections }]);
    setComboPicking(null);
    toast.success('Combo adicionado ao carrinho');
  };

  const subtotal = useMemo(
    () => cart.reduce((s, c) => s + effUnit(c.basePrice ?? c.price, c.tiers, c.quantity || 1) * (c.quantity || 1), 0),
    [cart]
  );
  const totalWithFee = subtotal * 1.05;
  const depositNow = Math.round((subtotal / 2) * 100) / 100;
  const itemsCount = cart.reduce((s, c) => s + (c.quantity || 1), 0);

  const setPicker = (pid, patch) =>
    setPickers((prev) => ({ ...prev, [pid]: { ...(prev[pid] || {}), ...patch } }));

  const addToCart = (product) => {
    const pick = pickers[product.id] || {};
    const size = pick.size || autoPick(product.sizes);
    const color = pick.color || autoPick(product.colors);
    const err = missingOption(product, size, color);
    if (err) { toast.error(err); return; }
    const line = {
      product_id: product.id,
      product_name: product.name,
      size,
      color,
      quantity: 1,
      price: product.price || 0,
      basePrice: product.price || 0,
      tiers: product.promo_tiers || [],
    };
    const key = lineKey(line);
    setCart((prev) => {
      const idx = prev.findIndex((l) => lineKey(l) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + 1 };
        return next;
      }
      return [...prev, line];
    });
    toast.success(t('store.addedToCart', { name: product.name }));
  };

  const changeQty = (key, delta) => {
    setCart((prev) =>
      prev
        .map((l) => (lineKey(l) === key ? { ...l, quantity: Math.max(1, (l.quantity || 1) + delta) } : l))
    );
  };

  const removeLine = (key) => {
    setCart((prev) => prev.filter((l) => lineKey(l) !== key));
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(t('common.error'));
      return;
    }
    if (cart.length === 0) {
      toast.error(t('store.cartEmpty'));
      return;
    }
    setSubmitting(true);
    try {
      const { data: order } = await axios.post(`${API}/public/store/cart-order`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        payment_mode: paymentMode,
        items: cart.filter((c) => !c.is_combo).map((c) => ({
          product_id: c.product_id,
          size: c.size,
          color: c.color,
          quantity: c.quantity,
        })),
        combos: cart.filter((c) => c.is_combo).map((c) => ({ combo_id: c.combo_id, quantity: 1, selections: c.selections })),
      });
      const { data: checkout } = await axios.post(`${API}/public/store/checkout`, {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      if (checkout.url) {
        window.location.assign(checkout.url);
      } else {
        toast.error(t('common.errorProcessing'));
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.errorProcessing'));
      setSubmitting(false);
    }
  };

  const handleZelle = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(t('common.error'));
      return;
    }
    if (cart.length === 0) { toast.error(t('store.cartEmpty')); return; }
    setSubmitting(true);
    try {
      const { data: order } = await axios.post(`${API}/public/store/cart-order`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        payment_mode: paymentMode,
        items: cart.filter((c) => !c.is_combo).map((c) => ({ product_id: c.product_id, size: c.size, color: c.color, quantity: c.quantity })),
        combos: cart.filter((c) => c.is_combo).map((c) => ({ combo_id: c.combo_id, quantity: 1, selections: c.selections })),
      });
      setZelleOrder(order);
      setZelleStep(true);
      navigator.clipboard?.writeText('5618596830')?.catch(() => {});
      toast.success('Pedido criado! Envie o valor via Zelle para 5618596830 e anexe o comprovante.');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.errorProcessing'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleZelleUpload = async (file) => {
    if (!file || !zelleOrder) return;
    setUploadingZelle(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/public/order/zelle-receipt?collection=public_store_orders&order_id=${zelleOrder.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Comprovante enviado! Pagamento confirmado.');
      setCart([]);
      setZelleStep(false);
      setZelleOrder(null);
      setShowCheckout(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.errorProcessing'));
    } finally {
      setUploadingZelle(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-white border-b border-[#E6E2D8] px-4 py-4 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E85D04] text-white flex items-center justify-center font-bold" data-testid="public-store-logo">TK</div>
            <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCheckout(true)}
              className="relative px-3 py-2 rounded-lg bg-[#1A1A1A] text-white text-sm flex items-center gap-2 hover:bg-[#333]"
              data-testid="public-store-open-cart"
            >
              <ShoppingCart size={18} /> {t('store.cart')}
              {itemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#E85D04] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="public-store-cart-count">
                  {itemsCount}
                </span>
              )}
            </button>
            <LanguageToggle />
            <Link to="/login" className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium" data-testid="public-store-login-link">
              {t('common.doLogin')}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#73706A] hover:text-[#1A1A1A] mb-6" data-testid="public-store-back-link">
          <ArrowLeft size={16} /> {t('common.back')}
        </Link>

        {isSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-900 rounded-xl p-5 flex items-start gap-3" data-testid="public-store-success-banner">
            <CheckCircle size={24} className="flex-shrink-0 mt-0.5" weight="fill" />
            <div>
              <p className="font-semibold">{t('store.successTitle')}</p>
              <p className="text-sm mt-1">{t('store.successDesc')}</p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-bold text-[#1A1A1A]" data-testid="public-store-title">
            {t('store.title')}
          </h1>
          <p className="text-[#73706A] mt-2">{t('store.subtitle')}</p>
        </div>

        {loading ? (
          <p className="text-[#73706A]" data-testid="public-store-loading">{t('store.loading')}</p>
        ) : products.length === 0 ? (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-10 text-center" data-testid="public-store-empty">
            <p className="text-[#73706A]">{t('store.noProducts')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="public-store-grid">
            {products.map((product, i) => {
              const pick = pickers[product.id] || {};
              return (
                <div
                  key={product.id}
                  className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden flex flex-col transition-shadow hover:shadow-md"
                  data-testid={`public-store-product-${i}`}
                >
                  {product.has_image ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/product-image/${product.id}/${activeImg[product.id] || 0}`}
                      alt={product.name}
                      className="w-full h-56 object-contain bg-[#F5F1E8]"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      data-testid={`public-store-product-image-${i}`}
                    />
                  ) : (
                    <div className="w-full h-56 bg-[#F5F1E8] flex items-center justify-center text-[#C7C0B0]">
                      <ShoppingCart size={40} />
                    </div>
                  )}
                  {product.image_count > 1 && (
                    <div className="flex gap-1.5 px-4 pt-3 flex-wrap" data-testid={`public-store-gallery-${i}`}>
                      {Array.from({ length: product.image_count }).map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveImg((p) => ({ ...p, [product.id]: idx }))}
                          className={`w-10 h-10 rounded-md overflow-hidden border-2 ${(activeImg[product.id] || 0) === idx ? 'border-[#E85D04]' : 'border-[#E6E2D8]'}`}
                          data-testid={`public-store-thumb-${i}-${idx}`}
                        >
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/product-image/${product.id}/${idx}`} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-semibold text-[#1A1A1A]">{product.name}</h3>
                    {product.description && (
                      <p className="text-xs text-[#73706A] mt-1 line-clamp-2">{product.description}</p>
                    )}
                    <p className="text-2xl font-bold text-[#E85D04] mt-3">$ {(product.price || 0).toFixed(2)}</p>

                    {(product.sizes?.length > 0 || product.colors?.length > 0) && (
                      <div className="mt-3 space-y-3">
                        {product.sizes?.length > 0 && (
                          <OptionChips label={t('store.size')} options={product.sizes} value={pick.size || autoPick(product.sizes)} onChange={(v) => setPicker(product.id, { size: v })} testid={`public-store-pick-size-${i}`} />
                        )}
                        {product.colors?.length > 0 && (
                          <OptionChips label={t('store.color')} options={product.colors} value={pick.color || autoPick(product.colors)} onChange={(v) => setPicker(product.id, { color: v })} testid={`public-store-pick-color-${i}`} />
                        )}
                      </div>
                    )}

                    <Button
                      className="mt-4 w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                      onClick={() => addToCart(product)}
                      data-testid={`public-store-add-${i}`}
                    >
                      <ShoppingCart size={16} className="mr-2" /> {t('store.addToCart')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Promoções / Combos */}
        {!loading && combos.length > 0 && (
          <div className="mt-12" data-testid="public-store-combos-section">
            <h2 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-[#1A1A1A]">Promoções</h2>
            <p className="text-[#73706A] mt-1 mb-5">Combos especiais com preço fechado.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {combos.map((combo, i) => (
                <div key={combo.id} className="bg-white border-2 border-[#E85D04]/30 rounded-xl overflow-hidden flex flex-col" data-testid={`public-store-combo-${i}`}>
                  {combo.has_image && (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/combo-image/${combo.id}/0`} alt={combo.name} className="w-full h-48 object-contain bg-[#F5F1E8]" onError={(e) => { e.target.style.display = 'none'; }} />
                  )}
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-[#E85D04] text-white px-2 py-0.5 rounded">Promoção</span>
                    </div>
                    <h3 className="font-semibold text-[#1A1A1A] mt-2">{combo.name}</h3>
                    {combo.description && <p className="text-xs text-[#73706A] mt-1">{combo.description}</p>}
                    <ul className="text-sm text-[#1A1A1A] mt-2 space-y-0.5">
                      {combo.items.map((it, idx) => (<li key={idx}>• {it.quantity}x {it.product_name}</li>))}
                    </ul>
                    <p className="text-2xl font-bold text-[#E85D04] mt-3">$ {(combo.price || 0).toFixed(2)}</p>
                    <Button className="mt-4 w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white" onClick={() => setComboPicking(combo)} data-testid={`public-store-combo-add-${i}`}>
                      <ShoppingCart size={16} className="mr-2" /> Escolher tamanhos e cores
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <ComboPickerDialog combo={comboPicking} onClose={() => setComboPicking(null)} onConfirm={(sel) => addComboToCart(comboPicking, sel)} />

      {/* Cart Drawer */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCheckout(false)} data-testid="public-store-modal-overlay">
          <div
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="public-store-modal"
          >
            <div className="p-6 border-b border-[#E6E2D8] flex items-center justify-between">
              <div>
                <h2 className="text-xl font-['Outfit'] font-bold text-[#1A1A1A]">{t('store.cart')}</h2>
                <p className="text-sm text-[#73706A] mt-1">{t('store.items', { count: itemsCount })}</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="text-[#73706A] hover:text-[#1A1A1A] text-2xl leading-none" data-testid="public-store-modal-close">&times;</button>
            </div>

            <div className="p-6 space-y-4">
              {cart.length === 0 ? (
                <p className="text-center text-[#73706A] py-6" data-testid="public-store-cart-empty">{t('store.cartEmpty')}</p>
              ) : (
                <div className="space-y-3" data-testid="public-store-cart-list">
                  {cart.map((l, idx) => {
                    const k = lineKey(l);
                    return (
                      <div key={k} className="flex items-start gap-3 p-3 border border-[#E6E2D8] rounded-lg" data-testid={`public-store-cart-line-${idx}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1A1A1A] truncate">{l.product_name}</p>
                          <CartLineOptions line={l} />
                          <p className="text-sm font-semibold text-[#E85D04] mt-1">$ {(effUnit(l.basePrice ?? l.price, l.tiers, l.quantity) * l.quantity).toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {!l.is_combo && (<>
                          <button onClick={() => changeQty(k, -1)} className="w-7 h-7 rounded border border-[#E6E2D8] flex items-center justify-center text-[#73706A] hover:bg-[#F5F1E8]" data-testid={`public-store-cart-dec-${idx}`}>
                            <Minus size={14} />
                          </button>
                          <span className="w-6 text-center text-sm font-semibold" data-testid={`public-store-cart-qty-${idx}`}>{l.quantity}</span>
                          <button onClick={() => changeQty(k, 1)} className="w-7 h-7 rounded border border-[#E6E2D8] flex items-center justify-center text-[#73706A] hover:bg-[#F5F1E8]" data-testid={`public-store-cart-inc-${idx}`}>
                            <Plus size={14} />
                          </button>
                          </>)}
                          <button onClick={() => removeLine(k)} className="ml-1 w-7 h-7 rounded text-[#9B2226] hover:bg-red-50 flex items-center justify-center" data-testid={`public-store-cart-remove-${idx}`}>
                            <Trash size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {cart.length > 0 && !zelleStep && (
                <form onSubmit={handleCheckout} className="space-y-3 pt-3 border-t border-[#E6E2D8]">
                  <div>
                    <Label htmlFor="ps-name">{t('common.fullName')} *</Label>
                    <Input id="ps-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="public-store-input-name" />
                  </div>
                  <div>
                    <Label htmlFor="ps-email">{t('common.email')} *</Label>
                    <Input id="ps-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="public-store-input-email" />
                  </div>
                  <div>
                    <Label htmlFor="ps-phone">{t('store.phoneWhatsapp')} *</Label>
                    <Input id="ps-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="public-store-input-phone" />
                  </div>

                  <div>
                    <Label>Forma de pagamento</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <button type="button" onClick={() => setPaymentMode('full')} className={`p-2.5 rounded-lg border text-sm text-left transition-colors ${paymentMode === 'full' ? 'border-[#E85D04] bg-orange-50' : 'border-[#E6E2D8]'}`} data-testid="public-store-mode-full">
                        <span className="font-medium">Valor total</span>
                        <span className="block text-[11px] text-[#73706A]">Pagar tudo agora</span>
                      </button>
                      <button type="button" onClick={() => setPaymentMode('deposit')} className={`p-2.5 rounded-lg border text-sm text-left transition-colors ${paymentMode === 'deposit' ? 'border-[#E85D04] bg-orange-50' : 'border-[#E6E2D8]'}`} data-testid="public-store-mode-deposit">
                        <span className="font-medium">Pré-reserva 50%</span>
                        <span className="block text-[11px] text-[#73706A]">Metade agora, metade na entrega</span>
                      </button>
                    </div>
                  </div>

                  <div className="bg-[#F5F1E8] rounded-lg p-4 text-sm space-y-1">
                    <div className="flex justify-between text-[#73706A]">
                      <span>{t('common.subtotal')}</span>
                      <span>$ {subtotal.toFixed(2)}</span>
                    </div>
                    {paymentMode === 'deposit' && (
                      <div className="flex justify-between text-[#E85D04] font-medium" data-testid="public-store-deposit-row">
                        <span>Pagar agora (50%)</span>
                        <span>$ {depositNow.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-[#73706A]">
                      <span>{t('common.cardFee')}</span>
                      <span>$ {((paymentMode === 'deposit' ? depositNow : subtotal) * 0.05).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#1A1A1A] font-bold text-base pt-2 border-t border-[#E6E2D8]" data-testid="public-store-total">
                      <span>Cobrança no cartão agora</span>
                      <span>$ {((paymentMode === 'deposit' ? depositNow : subtotal) * 1.05).toFixed(2)}</span>
                    </div>
                    {paymentMode === 'deposit' && (
                      <p className="text-[11px] text-[#73706A] pt-1">Saldo de $ {(subtotal - depositNow).toFixed(2)} será cobrado na entrega.</p>
                    )}
                  </div>

                  <Button type="submit" className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white" disabled={submitting} data-testid="public-store-submit-checkout">
                    <CreditCard size={18} className="mr-2" />
                    {submitting ? t('store.processing') : t('common.payWithCard')}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleZelle} className="w-full border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white" disabled={submitting} data-testid="public-store-submit-zelle">
                    Pagar via Zelle
                  </Button>
                  <p className="text-xs text-[#73706A] text-center">{t('common.secure')}</p>
                </form>
              )}

              {cart.length > 0 && zelleStep && (
                <div className="space-y-3 pt-3 border-t border-[#E6E2D8]" data-testid="public-store-zelle-step">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                    <p className="font-semibold">Pague via Zelle</p>
                    <p className="mt-1">Envie <strong>$ {(paymentMode === 'deposit' ? depositNow : subtotal).toFixed(2)}</strong> para o número Zelle:</p>
                    <p className="text-lg font-bold mt-1">5618596830</p>
                    <p className="text-[11px] mt-1">Depois anexe o comprovante abaixo para confirmar.</p>
                  </div>
                  <label className="block">
                    <span className="text-sm font-medium">Comprovante Zelle *</span>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => handleZelleUpload(e.target.files?.[0])} disabled={uploadingZelle} className="mt-1 block w-full text-sm border border-[#E6E2D8] rounded p-2 file:mr-2 file:px-3 file:py-1 file:rounded file:border-0 file:bg-[#0077B6] file:text-white" data-testid="public-store-zelle-upload" />
                  </label>
                  {uploadingZelle && <p className="text-sm text-[#73706A]">Enviando...</p>}
                  <Button type="button" variant="outline" onClick={() => { setZelleStep(false); setZelleOrder(null); }} className="w-full" data-testid="public-store-zelle-cancel">
                    Voltar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
